import React, { useState, useEffect } from 'react';
import api from '../../../services/api';
import '../../../styles/CampaignWizard.css';
// Import FiIcons from react-icons
import { FiSearch, FiFilter, FiX, FiUsers, FiCheck, FiTrash2, FiUpload, FiChevronDown } from 'react-icons/fi';

const LeadSelection = ({ selectedLeads = [], onChange }) => {
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState({
    status: '',
    company: '',
    tags: ''
  });
  
  // Fetch clients
  useEffect(() => {
    const fetchClients = async () => {
      try {
        setLoading(true);
        const response = await api.get('/clients');
        setClients(response.data);
      } catch (error) {
        console.error('Error fetching clients:', error);
      } finally {
        setLoading(false);
      }
    };
    
    fetchClients();
  }, []);
  
  const handleSearch = (e) => {
    setSearch(e.target.value);
  };
  
  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters(prev => ({
      ...prev,
      [name]: value
    }));
  };
  
  const clearFilters = () => {
    setFilters({
      status: '',
      company: '',
      tags: ''
    });
    setSearch('');
  };
  
  const isSelected = (clientId) => {
    return selectedLeads.some(lead => lead._id === clientId);
  };
  
  const toggleSelection = (client) => {
    if (isSelected(client._id)) {
      onChange(selectedLeads.filter(lead => lead._id !== client._id));
    } else {
      onChange([...selectedLeads, client]);
    }
  };
  
  const selectAll = () => {
    onChange(filteredClients);
  };
  
  const clearSelection = () => {
    onChange([]);
  };
  
  // Filter clients based on search and filters
  const filteredClients = clients.filter(client => {
    // Search filter
    const matchesSearch = search === '' || 
      client.name?.toLowerCase().includes(search.toLowerCase()) ||
      client.email?.toLowerCase().includes(search.toLowerCase()) ||
      (client.company && client.company.toLowerCase().includes(search.toLowerCase()));
    
    // Status filter
    const matchesStatus = filters.status === '' || client.status === filters.status;
    
    // Company filter
    const matchesCompany = filters.company === '' || 
      (client.company && client.company.toLowerCase().includes(filters.company.toLowerCase()));
    
    // Tags filter
    const matchesTags = filters.tags === '' || 
      (client.tags && client.tags.some(tag => 
        tag.toLowerCase().includes(filters.tags.toLowerCase())
      ));
    
    return matchesSearch && matchesStatus && matchesCompany && matchesTags;
  });
  
  return (
    <div className="wizard-step-container">
      <div className="wizard-step-header">
        <h2>Select Leads</h2>
        <p>Choose the leads you want to include in this campaign.</p>
      </div>
      
      <div className="lead-selection-container">
        <div className="lead-selection-header">
          <div className="selection-count">
            <FiUsers className="icon" />
            <span>{selectedLeads.length} leads selected</span>
          </div>
          
          <div className="lead-actions">
            <button className="btn btn-sm btn-outline" onClick={clearSelection}>
              <FiTrash2 className="btn-icon" />
              Clear Selection
            </button>
            
            <button className="btn btn-sm btn-outline" onClick={selectAll}>
              <FiCheck className="btn-icon" />
              Select All Filtered
            </button>
            
            <button className="btn btn-sm btn-primary">
              <FiUpload className="btn-icon" />
              Import Leads
            </button>
          </div>
        </div>
        
        <div className="lead-search-filters">
          <div className="search-container">
            <FiSearch className="search-icon" />
            <input
              type="text"
              value={search}
              onChange={handleSearch}
              placeholder="Search leads..."
              className="search-input"
            />
            {search && (
              <button className="search-clear" onClick={() => setSearch('')}>
                <FiX />
              </button>
            )}
          </div>
          
          <div className="filter-container">
            <button 
              className={`filter-toggle ${showFilters ? 'active' : ''}`} 
              onClick={() => setShowFilters(!showFilters)}
            >
              <FiFilter />
              Filters
              <FiChevronDown className={`dropdown-arrow ${showFilters ? 'open' : ''}`} />
            </button>
            
            {showFilters && (
              <div className="filter-dropdown">
                <div className="filter-group">
                  <label>Status</label>
                  <select
                    name="status"
                    value={filters.status}
                    onChange={handleFilterChange}
                  >
                    <option value="">All Statuses</option>
                    <option value="Active">Active</option>
                    <option value="Inactive">Inactive</option>
                    <option value="Lead">Lead</option>
                  </select>
                </div>
                
                <div className="filter-group">
                  <label>Company</label>
                  <input
                    type="text"
                    name="company"
                    value={filters.company}
                    onChange={handleFilterChange}
                    placeholder="Filter by company..."
                  />
                </div>
                
                <div className="filter-group">
                  <label>Tags</label>
                  <input
                    type="text"
                    name="tags"
                    value={filters.tags}
                    onChange={handleFilterChange}
                    placeholder="Filter by tags..."
                  />
                </div>
                
                <div className="filter-actions">
                  <button className="btn btn-sm btn-outline" onClick={clearFilters}>
                    Clear Filters
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
        
        {loading ? (
          <div className="loading-container">
            <div className="loading-spinner"></div>
            <p>Loading leads...</p>
          </div>
        ) : (
          <>
            <div className="lead-selection-table">
              <table>
                <thead>
                  <tr>
                    <th className="checkbox-column"></th>
                    <th>Name</th>
                    <th>Email</th>
                    <th>Company</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredClients.length > 0 ? (
                    filteredClients.map(client => (
                      <tr 
                        key={client._id} 
                        className={isSelected(client._id) ? 'selected' : ''}
                        onClick={() => toggleSelection(client)}
                      >
                        <td className="checkbox-column">
                          <div className={`custom-checkbox ${isSelected(client._id) ? 'checked' : ''}`}>
                            {isSelected(client._id) && <FiCheck />}
                          </div>
                        </td>
                        <td>{client.name}</td>
                        <td>{client.email}</td>
                        <td>{client.company || '-'}</td>
                        <td>
                          <span className={`status-badge status-${client.status?.toLowerCase()}`}>
                            {client.status}
                          </span>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="5" className="empty-table-message">
                        No leads match your filters. Try adjusting your search or filters.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
            
            <div className="lead-selection-footer">
              <div className="pagination">
                {/* Pagination would go here */}
              </div>
              
              <div className="selected-count">
                {selectedLeads.length} of {clients.length} leads selected
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default LeadSelection;